import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthSession } from "../../auth/keycloak.js";
import { favoriteApi } from "../../api/client.js";
import { invalidateDashboard } from "../dashboard/dashboard-cache.js";

const EMPTY: ReadonlySet<number> = new Set();

/**
 * The caller's favourite manual ids (persisted — ADR-011, the manual mirror of {@link useFacilityFavourites}). GET on
 * mount + **optimistic** add/remove, keyed by tenant + subject (per-user). The server already filters the list to
 * currently-viewable manuals; the view maps these ids onto its loaded manuals (the star + the "Favourites" chip) and
 * ignores any that don't resolve. Same return shape as before the swap (`{ favorites, toggle }`), so the cards/chip are
 * untouched.
 */
export function useManualFavorites(session: AuthSession | null) {
  const qc = useQueryClient();
  const key = ["manual-favourites", session?.realm, session?.sub] as const;
  // Per-manual serialization + explicit desired-state so a rapid add-then-remove can't commit out of order.
  const chains = useRef(new Map<number, Promise<unknown>>());
  const desired = useRef(new Map<number, boolean>());

  const query = useQuery({
    queryKey: key,
    enabled: session !== null,
    queryFn: async () => {
      const res = await favoriteApi.listUserManualFavourites();
      if (res.status !== 200) throw new Error(`GET /user/manual-favourites returned ${res.status}`);
      return new Set(res.body.manuals);
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ id, next }: { id: number; next: boolean }) => {
      const res = next
        ? await favoriteApi.addManualFavourite({ params: { id }, body: {} })
        : await favoriteApi.removeManualFavourite({ params: { id } });
      if (res.status !== 200) throw new Error(`manual favourite ${next ? "add" : "remove"} returned ${res.status}`);
    },
    // Optimistic — flip ONLY this id; on error revert ONLY this id (never restore the whole snapshot, which would
    // clobber a concurrent toggle of a DIFFERENT manual); reconcile on settle.
    onMutate: async ({ id, next }) => {
      await qc.cancelQueries({ queryKey: key });
      const had = qc.getQueryData<ReadonlySet<number>>(key)?.has(id) ?? false;
      qc.setQueryData<ReadonlySet<number>>(key, (curr) => {
        const s = new Set(curr ?? EMPTY);
        if (next) s.add(id);
        else s.delete(id);
        return s;
      });
      return { had };
    },
    onError: (_e, vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData<ReadonlySet<number>>(key, (curr) => {
        const s = new Set(curr ?? EMPTY);
        if (ctx.had) s.add(vars.id);
        else s.delete(vars.id);
        return s;
      });
    },
    onSettled: (_d, _e, vars) => {
      // Clear the desired-state tracker ONLY when this settled mutation IS the last-intended one — else a still-queued
      // toggle for the same manual would fall back to the mid-flight cache and enqueue the wrong action.
      if (desired.current.get(vars.id) === vars.next) desired.current.delete(vars.id);
      void qc.invalidateQueries({ queryKey: key });
      // Keep the global "Manual favourites" rail (a separate detailed query) in sync when toggling from the manuals view.
      void qc.invalidateQueries({ queryKey: ["manual-favourites-list", session?.realm, session?.sub] });
      invalidateDashboard(qc, session); // the Favourite manuals page reads `favouritedAt` off the dashboard list (ADR-034)
    },
  });

  const favorites = query.data ?? EMPTY;
  return {
    favorites,
    /** Toggle a manual's favourite (optimistic). Serialized per manual + tracks the DESIRED final state, so a rapid
     *  add-then-remove commits in order and lands in the last-clicked state. */
    toggle: (id: number) => {
      const next = !(desired.current.get(id) ?? favorites.has(id));
      desired.current.set(id, next);
      const prev = chains.current.get(id) ?? Promise.resolve();
      chains.current.set(id, prev.catch(() => {}).then(() => mutation.mutateAsync({ id, next })));
    },
  };
}
